import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Search,
  ListFilter,
  MessageSquare,
  Mail,
  Phone,
  ArrowDownUp,
  ArrowUp,
  ArrowDown,
  X,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { DateRange } from 'react-day-picker'
import { Button } from '@/components/ui/button'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { DateRangePicker } from '@/components/ui/date-range-picker'

interface ThreadListSearchProps {
  searchQuery: string
  onSearchChange: (query: string) => void
  onFiltersChange?: (filters: {
    messageTypes: string[]
    directions: string[]
    dateRange?: DateRange
    sortOption: 'asc' | 'desc' | null
  }) => void
  loading?: boolean
}

type SortOption = 'asc' | 'desc' | null

export const ThreadListSearch: React.FC<ThreadListSearchProps> = ({
  searchQuery,
  onSearchChange,
  onFiltersChange,
  loading = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [messageTypes, setMessageTypes] = useState<string[]>([])
  const [directions, setDirections] = useState<string[]>([])
  const [dateRange, setDateRange] = useState<DateRange | undefined>()
  const [sortOption, setSortOption] = useState<SortOption>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const filterContentRef = useRef<HTMLDivElement>(null)

  // Handle click outside to collapse
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      // Don't collapse if clicking inside the filter container
      if (containerRef.current && containerRef.current.contains(target)) {
        return
      }

      // Don't collapse if clicking on a thread preview (allows selection to happen)
      if (target instanceof Element) {
        const threadPreview = target.closest('[data-thread-preview]')
        if (threadPreview) {
          return
        }
      }

      setIsExpanded(false)
    }

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isExpanded])

  const handleClearFilters = () => {
    setMessageTypes([])
    setDirections([])
    setDateRange(undefined)
    setSortOption(null)
    onFiltersChange?.({
      messageTypes: [],
      directions: [],
      dateRange: undefined,
      sortOption: null,
    })
  }

  const handleSortClick = () => {
    const newSortOption =
      sortOption === null ? 'asc' : sortOption === 'asc' ? 'desc' : null
    setSortOption(newSortOption)
    onFiltersChange?.({
      messageTypes,
      directions,
      dateRange,
      sortOption: newSortOption,
    })
  }

  const handleDateRangeChange = useCallback(
    (newDateRange: DateRange | undefined) => {
      setDateRange(newDateRange)
      onFiltersChange?.({
        messageTypes,
        directions,
        dateRange: newDateRange,
        sortOption,
      })
    },
    [messageTypes, directions, sortOption, onFiltersChange],
  )

  const getSortIcon = () => {
    if (sortOption === 'asc') {
      return <ArrowUp className="w-4 h-4" />
    } else if (sortOption === 'desc') {
      return <ArrowDown className="w-4 h-4" />
    }
    return <ArrowDownUp className="w-4 h-4" />
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur()
      setIsExpanded(false)
    }
  }

  const handleMessageTypesChange = (newTypes: string[]) => {
    setMessageTypes(newTypes)
    onFiltersChange?.({
      messageTypes: newTypes,
      directions,
      dateRange,
      sortOption,
    })
  }

  const handleDirectionsChange = (newDirections: string[]) => {
    setDirections(newDirections)
    onFiltersChange?.({
      messageTypes,
      directions: newDirections,
      dateRange,
      sortOption,
    })
  }

  const hasActiveFilters =
    messageTypes.length > 0 || directions.length > 0 || dateRange?.from

  if (loading) {
    return (
      <div className="relative bg-white dark:bg-background flex-shrink-0 border-b border-gray-200 dark:border-border">
        <div className="h-16 px-4 flex items-center gap-2">
          <div className="h-9 flex-1 bg-gray-200 dark:bg-muted rounded-md animate-pulse" />
          <div className="h-9 w-9 bg-gray-200 dark:bg-muted rounded-md animate-pulse" />
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="relative bg-white dark:bg-background flex-shrink-0 border-b border-gray-200 dark:border-border"
    >
      {/* Search and filter row - always visible */}
      <div className="h-16 px-4 flex items-center gap-2 relative z-20">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-muted-foreground" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search conversations..."
            className="h-9 w-full pl-10 pr-10 py-2 text-sm border border-gray-300 dark:border-border dark:bg-background dark:text-foreground dark:placeholder:text-muted-foreground rounded-md focus:outline-none focus:ring-1 focus:ring-gray-700 dark:focus:ring-border focus:border-gray-700 dark:focus:border-border transition-colors duration-200"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-muted-foreground hover:text-gray-600 dark:hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setIsExpanded(!isExpanded)}
          className={`h-9 w-9 relative transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-700 focus:border-gray-700 focus-visible:ring-1 focus-visible:ring-gray-700 focus-visible:border-gray-700 ${isExpanded ? 'bg-gray-900 dark:!bg-foreground text-white dark:!text-background hover:bg-gray-900 dark:hover:!bg-foreground/90 hover:text-white border-gray-900 dark:!border-foreground' : 'border-gray-300 dark:border-border'}`}
        >
          <ListFilter className="w-4 h-4" />
          <AnimatePresence>
            {hasActiveFilters && (
              <motion.span
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 15,
                }}
                className="absolute -top-1 -left-1 w-2.5 h-2.5 bg-slate-500 rounded-full"
              />
            )}
          </AnimatePresence>
        </Button>
      </div>

      {/* Animated filter content */}
      <AnimatePresence mode="wait">
        {isExpanded && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{
              duration: 0.25,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            className="overflow-hidden will-change-[height]"
          >
            {/* Border separator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="px-4"
            >
              <div className="border-t border-gray-100 dark:border-border" />
            </motion.div>

            {/* Filter rows with animation */}
            <motion.div
              ref={filterContentRef}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{
                duration: 0.2,
                ease: [0.25, 0.1, 0.25, 1],
              }}
              className="px-4 pt-4 pb-4 space-y-3"
            >
              {/* Date range picker and sort button */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <DateRangePicker
                    date={dateRange}
                    onDateChange={handleDateRangeChange}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleSortClick}
                    className={`h-9 w-9 transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-700 focus:border-gray-700 ${sortOption !== null ? 'bg-gray-900 dark:!bg-foreground text-white dark:!text-background hover:bg-gray-900 dark:hover:!bg-foreground/90 hover:text-white border-gray-900 dark:!border-foreground' : 'border-gray-300 dark:border-border'}`}
                  >
                    {getSortIcon()}
                  </Button>
                </div>
              </div>

              {/* Message type toggles */}
              <div className="space-y-2">
                <div>
                  <ToggleGroup
                    type="multiple"
                    value={messageTypes}
                    onValueChange={handleMessageTypesChange}
                    variant="outline"
                  >
                    <ToggleGroupItem value="sms" aria-label="SMS">
                      <MessageSquare className="w-4 h-4" />
                      <span>SMS</span>
                    </ToggleGroupItem>
                    <ToggleGroupItem value="email" aria-label="Email">
                      <Mail className="w-4 h-4" />
                      <span>Email</span>
                    </ToggleGroupItem>
                    <ToggleGroupItem value="call" aria-label="Call">
                      <Phone className="w-4 h-4" />
                      <span>Call</span>
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </div>

              {/* Direction toggles */}
              <div className="space-y-2">
                <div>
                  <ToggleGroup
                    type="multiple"
                    value={directions}
                    onValueChange={handleDirectionsChange}
                    variant="outline"
                  >
                    <ToggleGroupItem value="inbound" aria-label="Inbound">
                      <ArrowDown className="w-4 h-4" />
                      <span>Inbound</span>
                    </ToggleGroupItem>
                    <ToggleGroupItem value="outbound" aria-label="Outbound">
                      <ArrowUp className="w-4 h-4" />
                      <span>Outbound</span>
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </div>

              {/* Clear button */}
              <div className="flex justify-start">
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  Clear filters
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
